import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import type { StatusTone } from '@/lib/statusTone';
import { statusToneTextClass } from '@/lib/statusTone';

interface DataListItem {
  /** Label for the property */
  label: string;
  /** Value to display (supports ReactNode for complex content) */
  value: ReactNode;
  /** Optional tone for value styling */
  tone?: StatusTone;
  /** If true, value uses font-mono (for codes/IDs/emails) */
  mono?: boolean;
  /** Layout style for the row */
  layout?: 'inline' | 'stacked';
  /** Optional class override for value */
  valueClassName?: string;
}

interface DataListSection {
  /** Optional section title */
  title?: string;
  /** Items within the section */
  items: DataListItem[];
}

interface DataListProps {
  /** Array of label-value pairs */
  items?: DataListItem[];
  /** Optional grouped sections */
  sections?: DataListSection[];
  /** Text size variant */
  size?: 'sm' | 'md';
  /** Additional class names */
  className?: string;
}

/**
 * Read-only key-value display for entity properties.
 *
 * Replaces repeated `<dl className="space-y-3"><div className="flex justify-between">...` patterns.
 * Use for property displays in Panel components, company registry info, site metadata.
 *
 * Visual treatment:
 * - space-y-3 between items
 * - Label: text-text-secondary
 * - Value: text-right font-medium text-text-primary
 * - Supports tone-based text colors
 * - Mono font option for codes
 *
 * @example
 * ```tsx
 * <DataList items={[
 *   { label: "Nome", value: person.name },
 *   { label: "Email", value: person.email, mono: true },
 *   { label: "Stato", value: person.status, tone: "success" },
 * ]} />
 *
 * <DataList size="sm" items={compactItems} />
 *
 * <DataList
 *   sections={[
 *     { title: "Informazioni", items: infoItems },
 *     { title: "Note", items: [{ label: "Note", value: notes, layout: "stacked" }] },
 *   ]}
 * />
 * ```
 */
export function DataList({ items, sections, size = 'md', className }: DataListProps) {
  const textSize = size === 'sm' ? 'text-xs' : 'text-sm';
  const sectionsToRender = sections && sections.length > 0 ? sections : items ? [{ items }] : [];

  if (sectionsToRender.length === 0) {
    return null;
  }

  return (
    <div className={cn('space-y-6', className)}>
      {sectionsToRender.map((section, sectionIndex) => (
        <div key={sectionIndex} className="space-y-3">
          {section.title ? (
            <p className="text-xs font-semibold uppercase tracking-wide text-text-secondary">
              {section.title}
            </p>
          ) : null}
          <dl className={cn('space-y-3', textSize)}>
            {section.items.map((item, index) => {
              const isStacked = item.layout === 'stacked';
              return (
                <div
                  key={index}
                  className={cn(
                    isStacked
                      ? 'flex flex-col items-start gap-1'
                      : 'flex items-center justify-between gap-4'
                  )}
                >
                  <dt className="text-text-secondary">{item.label}</dt>
                  <dd
                    className={cn(
                      isStacked
                        ? 'font-medium text-text-primary'
                        : 'text-right font-medium text-text-primary',
                      item.mono && 'font-mono tabular-nums',
                      item.tone && statusToneTextClass(item.tone),
                      item.valueClassName
                    )}
                  >
                    {item.value}
                  </dd>
                </div>
              );
            })}
          </dl>
        </div>
      ))}
    </div>
  );
}
